import { Guid } from '../../classes/guid';
import { ComparisonOperators } from '../../enums/comparison-operators.enum';
import { IfGateDateTimeFormatType } from '../../enums/if-gate/if-gate-date-time-format-type';
import { IfGateInOptions } from '../../enums/if-gate/if-gate-in-options';
import { ValidationDataTypes } from '../../enums/validation-task/validation-data-types';

/**
 * A single condition rule within an IfRuleGroup. Compares `value1` to `value2` using
 * the specified `operator` and `dataType`. Both values are resolved through the
 * expression parser before comparison, so field expressions are supported.
 */
export class IfRule {
	/** Unique identifier for this rule. */
	id = Guid();

	/**
	 * Left-hand side value. Typically an expression referencing a processingData field
	 * (e.g. `{{record.status}}`). Can also be a static literal.
	 */
	value1?: any;

	/**
	 * Right-hand side comparison value. Static literal, field expression, or an array
	 * when used with IN / NOT IN operators and `inOperator = FixedValues`.
	 */
	value2?: any;

	/**
	 * Comparison operator to apply between value1 and value2.
	 * @default ComparisonOperators.Equal ('===')
	 */
	operator = ComparisonOperators.Equal;

	/**
	 * Data type used for parsing and comparing the values.
	 * String=0, Number=1, Boolean=2, Object=3, DateTime=4.
	 * @default ValidationDataTypes.String
	 */
	dataType: ValidationDataTypes = ValidationDataTypes.String;

	/**
	 * Controls how `value2` is interpreted for IN / NOT IN operators.
	 * ArrayField=0: value2 is a field expression that resolves to an array at runtime.
	 * FixedValues=1: value2 is a literal array of values provided directly in the config.
	 * @default IfGateInOptions.FixedValues (1)
	 */
	inOperator: IfGateInOptions = IfGateInOptions.FixedValues;

	/**
	 * Date/time parsing format for DateTime dataType comparisons.
	 * ISO=0 (ISO 8601, default), Custom=1 (requires `dateFormat`).
	 * @default IfGateDateTimeFormatType.ISO (0)
	 */
	dateTimeFormatType: IfGateDateTimeFormatType = IfGateDateTimeFormatType.ISO;

	/**
	 * Custom Luxon date format string. Only used when `dateTimeFormatType = Custom (1)`.
	 * E.g. `"dd/MM/yyyy"` or `"dd/MM/yyyy HH:mm"`.
	 */
	dateFormat?: string;

	/**
	 * When true, string comparisons are case-insensitive (both values are uppercased before comparing).
	 * Applies to: Equal, NotEqual, In, NotIn, Regex, ARRAY_CONTAINS operators on String dataType.
	 */
	ignoreCase?: boolean;

	constructor(init?: Partial<IfRule>) {
		if (init) {
			Object.assign(this, init);
		}
		if (!this.id) {
			this.id = Guid();
		}
	}
}
